import React from "react";
import { formatIdDateTime } from "@/lib/format";
import {
  CaretDown, CaretRight, ClockClockwise,
  Plus, PencilSimple, ArrowsClockwise, Prohibit, Trash,
  ArrowCounterClockwise, ShieldCheck, WarningCircle, QrCode, Camera,
} from "@phosphor-icons/react";

// Map every possible audit action to a friendly Indonesian label + icon +
// color so a non-technical user can read the timeline like a story.
// Keys mirror `log_audit("...")` calls scattered across the backend
// routers — keep in sync when new actions are added.
const ACTION_META = {
  "spb.create":                { label: "SPB Dibuat", icon: Plus, color: "text-emerald-700", tone: "bg-emerald-50 border-emerald-300" },
  "spb.edit":                  { label: "Data SPB Diubah", icon: PencilSimple, color: "text-amber-700", tone: "bg-amber-50 border-amber-300" },
  "spb.status_change":         { label: "Status Diubah", icon: ArrowsClockwise, color: "text-blue-700", tone: "bg-blue-50 border-blue-300" },
  "spb.cancel":                { label: "Dibatalkan", icon: Prohibit, color: "text-red-700", tone: "bg-red-50 border-red-300" },
  "spb.restore":               { label: "Dipulihkan", icon: ArrowCounterClockwise, color: "text-teal-700", tone: "bg-teal-50 border-teal-300" },
  "spb.delete":                { label: "Dihapus Permanen", icon: Trash, color: "text-red-800", tone: "bg-red-100 border-red-400" },
  "spb.qr_sign":               { label: "Tanda Tangan QR", icon: QrCode, color: "text-violet-700", tone: "bg-violet-50 border-violet-300" },
  "spb.qr_backfill_pembuat":   { label: "QR PEMBUAT Ditambah Otomatis", icon: QrCode, color: "text-violet-700", tone: "bg-violet-50 border-violet-300" },
  "spb.verify_via_qr":         { label: "Diverifikasi via Scan QR", icon: ShieldCheck, color: "text-green-700", tone: "bg-green-50 border-green-300" },
  "spb.verify_failed":         { label: "Upaya Scan Gagal (Anti-Palsu)", icon: WarningCircle, color: "text-red-700", tone: "bg-red-50 border-red-300" },
  "proof.upload":              { label: "Foto Bukti Muat Diunggah", icon: Camera, color: "text-cyan-700", tone: "bg-cyan-50 border-cyan-300" },
};

// Field name → user-friendly Indonesian label. Used to translate raw DB
// column names inside `meta.changes` into something a driver/admin can read
// without needing to know the DB schema.
const FIELD_LABEL = {
  no_spb: "No. SPB",
  no_polisi: "No. Polisi",
  tanggal: "Tanggal SPB",
  distrik_id: "Distrik",
  distrik_kode: "Kode Distrik",
  distrik_nama: "Nama Distrik",
  plant_id: "Plant/Pabrik",
  plant_nama: "Nama Plant",
  produk_id: "Produk",
  produk_nama: "Nama Produk",
  nama_supir: "Nama Supir",
  jumlah: "Jumlah",
  satuan: "Satuan",
  nama_pelanggan: "Nama Pelanggan",
  alamat_pengiriman: "Alamat Pengiriman",
  transporter: "Transporter",
  status: "Status",
  jumlah_termuat: "Jumlah Termuat",
  catatan: "Catatan",
  pelanggans: "Daftar Pelanggan (multi-drop)",
  so_allocations: "Alokasi Sales Order",
  slot_antrian: "Slot Antrian",
};

const STATUS_LABEL = {
  belum_termuat: "Belum Termuat",
  sudah_termuat: "Sudah Termuat",
  mismatch: "Mismatch",
  diverifikasi: "Diverifikasi",
  dibatalkan: "Dibatalkan",
};

function _fmtVal(v) {
  if (v == null || v === "") return <em className="text-muted-foreground">(kosong)</em>;
  if (typeof v === "boolean") return v ? "Ya" : "Tidak";
  if (Array.isArray(v)) return `${v.length} item`;
  if (typeof v === "object") {
    // For nested objects like {kode, nama} — show key values inline.
    const parts = Object.entries(v).slice(0, 3).map(([k, val]) => `${k}: ${val}`).join(", ");
    return parts || "(objek)";
  }
  return String(v);
}

function _renderChanges(changes) {
  const entries = Object.entries(changes);
  if (entries.length === 0) return null;
  return (
    <ul className="mt-2 space-y-1 text-xs bg-white/70 border border-border p-2">
      {entries.map(([field, v]) => {
        const label = FIELD_LABEL[field] || field;
        const oldVal = field === "status" ? (STATUS_LABEL[v.old] || v.old) : v.old;
        const newVal = field === "status" ? (STATUS_LABEL[v.new] || v.new) : v.new;
        return (
          <li key={field} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-2">
            <span className="font-bold uppercase tracking-wider text-[10px] text-muted-foreground min-w-[140px]">
              {label}
            </span>
            <span className="flex flex-wrap items-baseline gap-2 font-mono">
              <span className="line-through text-red-600">{_fmtVal(oldVal)}</span>
              <span className="text-muted-foreground">→</span>
              <span className="text-emerald-700 font-bold">{_fmtVal(newVal)}</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function _extraDetails(entry) {
  const m = entry.meta || {};
  const rows = [];
  if (entry.action === "spb.create") {
    if (m.no_spb) rows.push(["No. SPB", m.no_spb]);
    if (m.has_signature != null) rows.push(["Dengan tanda tangan", m.has_signature ? "Ya" : "Tidak"]);
  }
  if (entry.action === "spb.status_change") {
    if (m.status) rows.push(["Status baru", STATUS_LABEL[m.status] || m.status]);
    if (m.jumlah_termuat != null) rows.push(["Jumlah termuat", `${m.jumlah_termuat}`]);
  }
  if (entry.action === "spb.cancel") {
    if (m.reason) rows.push(["Alasan", m.reason]);
    if (m.so_reverted != null) rows.push(["SO dikembalikan", `${m.so_reverted} alokasi`]);
  }
  if (entry.action === "spb.restore" && m.so_reapplied != null) {
    rows.push(["SO diterapkan ulang", `${m.so_reapplied} alokasi`]);
  }
  if (entry.action === "spb.verify_via_qr") {
    if (m.no_polisi) rows.push(["No. Polisi terscan", m.no_polisi]);
  }
  if (entry.action === "spb.verify_failed") {
    const reasons = {
      invalid_qr_format: "Format QR tidak dikenali",
      spb_not_found: "SPB tidak ditemukan di sistem",
      plate_mismatch: "No. polisi tidak cocok",
      cancelled: "SPB sudah dibatalkan",
      sig_url_spb_not_found: "SPB pada URL QR tidak ditemukan",
      sig_nonce_not_found: "Nonce tanda tangan tidak valid",
    };
    if (m.reason) rows.push(["Alasan gagal", reasons[m.reason] || m.reason]);
    if (m.qr_plate) rows.push(["Plat pada QR", m.qr_plate]);
    if (m.db_plate) rows.push(["Plat di DB", m.db_plate]);
  }
  if (entry.action === "spb.qr_sign" || entry.action === "spb.qr_backfill_pembuat") {
    if (m.nonce) rows.push(["Nonce", <span key="n" className="font-mono">{m.nonce.slice(0, 8)}…</span>]);
  }
  if (entry.action === "proof.upload") {
    if (m.kind) rows.push(["Jenis foto", m.kind === "bukti_muat" ? "Bukti Muat" : m.kind]);
    if (m.jumlah_termuat != null) rows.push(["Jumlah termuat", `${m.jumlah_termuat}`]);
    if (m.original_size && m.compressed_size) {
      const ratio = ((1 - m.compressed_size / m.original_size) * 100).toFixed(0);
      rows.push(["Ukuran foto", `${(m.compressed_size / 1024).toFixed(0)} KB (kompresi ${ratio}%)`]);
    }
  }
  if (rows.length === 0) return null;
  return (
    <dl className="mt-2 space-y-0.5 text-xs">
      {rows.map(([k, v]) => (
        // Key by field name `k` — action-detail rows are unique per label
        // within a single entry, so this is stable across re-renders.
        <div key={String(k)} className="flex gap-2">
          <dt className="text-muted-foreground uppercase tracking-wider text-[10px] min-w-[130px]">{k}</dt>
          <dd className="font-mono">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function HistoryItem({ entry }) {
  const info = ACTION_META[entry.action] || {
    label: entry.action, icon: ClockClockwise,
    color: "text-foreground", tone: "bg-secondary/30 border-border",
  };
  const Icon = info.icon;
  const changes = (entry.meta || {}).changes || {};
  const userDisplay = entry.user_name || entry.user_email || "sistem";
  return (
    <li className={`border-l-4 border-b border-border last:border-b-0 p-3 hover:bg-secondary/20 ${info.tone.split(" ").pop()}`}>
      <div className="flex items-start gap-3">
        <div className={`shrink-0 mt-0.5 ${info.color}`}>
          <Icon size={18} weight="bold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className={`text-xs font-black uppercase tracking-wider ${info.color}`}>
              {info.label}
            </span>
            <span className="text-[11px] text-muted-foreground font-mono whitespace-nowrap">
              {formatIdDateTime(entry.created_at)}
            </span>
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            oleh <span className="font-mono font-semibold text-foreground">{userDisplay}</span>
          </div>
          {_extraDetails(entry)}
          {_renderChanges(changes)}
        </div>
      </div>
    </li>
  );
}

// Collapsible audit-trail panel showing every action taken on an SPB.
// Extracted from SPBDetail so the parent stays focused on orchestration.
export default function HistoryPanel({ history, open, onToggle }) {
  return (
    <div className="border border-border bg-card mb-8 no-print" data-testid="history-panel">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-secondary/40 text-left"
        data-testid="history-toggle"
      >
        <div className="flex items-center gap-3">
          <ClockClockwise size={20} weight="bold" />
          <div>
            <div className="overline">Riwayat Perubahan SPB</div>
            <div className="text-sm font-semibold">
              {history.length} aktivitas
              {history[0] && (
                <>
                  {" · terakhir "}
                  <span className="font-mono text-muted-foreground">
                    {formatIdDateTime(history[0].created_at)}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        {open ? <CaretDown size={16} /> : <CaretRight size={16} />}
      </button>
      {open && (
        <div className="border-t border-border" data-testid="history-list">
          {history.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              Belum ada riwayat.
            </div>
          ) : (
            <ul>
              {history.map((h) => (
                <HistoryItem key={h.id} entry={h} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
